import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CreateEditModal from 'common/components/modules/modal-wrapper';
import classNames from 'classnames';
import Loader from 'common/components/elements/loader/circle';
import CropImage from './modules/crop-image';
// import InputRange from '../input-range';
import Button from 'common/components/elements/buttons/primary';

const CropImageModal = ({
   isMobile,
   onCloseModal,
   initialFile,
   initialImageSrc = null,
   onSave,
   aspectRatio,
   callbackAfterFileRead,
   showDifferentViews = false,
   minCropBoxWidth = 20,
   minCropBoxHeight = 20,
   initialAspectRatio,
}) => {
   const [imageSrc, setImageSrc] = useState(initialImageSrc)
   const [rangeValue, setRangeValue] = useState(0)
   const cropperRef = useRef(null)
   const [isLoadingSaveButton, setIsLoadingSaveButton] = useState(false)

   const initialFileMimeType = useRef('image/png')
   const initialFileType = useRef('png')
   const initialFileName = useRef('png')


   useEffect(() => {
      if(!initialFile) return;


      if(initialFile.type) {
         initialFileMimeType.current = initialFile.type
      }

      if(initialFile.name) {
         initialFileName.current = initialFile.name
         try {
            const splittedFileName = initialFile.name.split('.');
            const fileType = splittedFileName[splittedFileName.length - 1]?.toLowerCase()
            if(fileType) {
               initialFileType.current = fileType
            }
         } catch (error) {

         }
      }

      const reader = new FileReader();

      reader.onload = (e) => {
         setImageSrc(e.target?.result)

         if(callbackAfterFileRead) {
            callbackAfterFileRead()
         }

      };

      // Read the file as a Data URL
      reader.readAsDataURL(initialFile);

   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   const handleClickSave = () => {
      setIsLoadingSaveButton(true)
      // const originalWidth = cropperRef.current.getImageData().naturalWidth;
      // const originalHeight = cropperRef.current.getImageData().naturalHeight;

      const croppedCanvas = cropperRef.current?.getCroppedCanvas({
         // width: originalWidth,
         // height: originalHeight,
         imageSmoothingEnabled: true,
         imageSmoothingQuality: 'high',
      });

      croppedCanvas?.toBlob((blob) => {
         const file = new File([blob], initialFileName.current, { type: initialFileMimeType.current });

         onSave(file);
         setIsLoadingSaveButton(false);
      }, initialFileMimeType.current, 1);

   }

   const onZoomIn = (value) => {
      if(value >= 100 && rangeValue === 100) {
         return null
      }

      if(cropperRef.current) {
         if(value - rangeValue > 10) {
            const salt = (value - rangeValue) / 10
            for(let index = 1; index < salt; index++) {

               cropperRef.current.zoom(0.1)
            }
         } else {
            cropperRef.current.zoom(0.1)
         }
      }
   }

   const onZoomOut = (value) => {
      if(value <= 0 && rangeValue === 0) {
         return null
      }
      if(cropperRef.current) {

         if(rangeValue - value  > 10) {

            const salt = (rangeValue - value) / 10

            for(let index = 0; index < salt - 1; index++) {
               cropperRef.current.zoom(-0.1)
            }
         } else {
            cropperRef.current.zoom(-0.1)
         }
      }
   }

   // eslint-disable-next-line no-unused-vars
   const handleZoomRangeChange = (name, value) => {
      let val = Number(value)
      if(value >= 100) {
         val = 100
      }
      if(val <= 0) {
         val = 0
      }
      if(val > rangeValue) {
         if(val === 100) {
            setRangeValue(100)
            onZoomIn(100)
            return
         }
         onZoomIn(val + 10)
      } else {
         onZoomOut(val - 10)
      }
      setRangeValue(val)
   }

   const onClickZoomOut = () => {
      const newValue = rangeValue - 10
      if(rangeValue === 0 || newValue <= 0) {
         onZoomOut(0)
         setRangeValue(0)
         return
      }

      setRangeValue(newValue)
      onZoomOut(newValue)
   }

   const onClickZoomIn = () => {
      const newValue = rangeValue + 10
      if(rangeValue === 100 || newValue >= 100) {
         setRangeValue(100)
         onZoomIn(100)
         return
      }
      setRangeValue(newValue)
      onZoomIn(newValue)
   }
   const onClickFlip = () => {
      const scaleRatio = cropperRef.current.getImageData()?.scaleX ? cropperRef.current.getImageData().scaleX * -1 : -1
      cropperRef.current.scaleX(scaleRatio)
   }

   const handleClickRevertChanges = () => {
      if(!cropperRef.current) return;

      cropperRef.current.reset()
      setRangeValue(0)
   }

   const onClickRotate = (diraction) => {
      if(!cropperRef.current) return;

      switch (diraction) {
         case 'left':
            cropperRef.current.rotate(-90)
            break;

         default:
            cropperRef.current.rotate(90)
            break;
      }

   }


   return (
      <CreateEditModal
         type='confirm'
         isMobile={ isMobile }
         onCloseModal={ onCloseModal }
         title='Crop image'
         withoutCloseButton={ false }
         // contentWidth='80%'
         withoutCancelButton
         buttonText='Save'
         nonPrimaryColor={ false }
         confirmButtonProperties={ {
            backgroundColor: null,
         } }
         buttonClassName='w-full mt-3'
         action={ handleClickSave }
         modalWrapperClassName='flex flex-col'
         className='!px-6 !pb-6 max-h-full flex-1 overflow-y-hidden'
         closeBtnClassName='!w-[fit-content] shrink-0'
         otherClassName={ !isMobile ? '!w-[1134px] !max-w-[80%]' : '' }
         isLoading={ isLoadingSaveButton }
      >
         <div
            className='w-full flex flex-col items-center gap-4 mt-7 max-h-full flex-1 overflow-auto'
         >
            <div
               className={ classNames({
                  'w-full rounded-xlg overflow-hidden bg-cover bg-center bg-no-repeat bg-panel dark:bg-panel-dark cropper-wrapper': true,
                  'max-h-[480px]': !isMobile,
                  'max-h-[220px]': isMobile,
                  'cropper-different-views': showDifferentViews,
               }) }
               style={ {
                  backdropFilter: 'blur(4px)',
                  background: 'none',
               } }
            >
               {
                  imageSrc ? (
                     <CropImage
                        imageSrc={ imageSrc }
                        cropperRef={ cropperRef }
                        aspectRatio={ aspectRatio }
                        minCropBoxHeight={ minCropBoxHeight }
                        minCropBoxWidth={ minCropBoxWidth }
                        initialAspectRatio={ initialAspectRatio }
                     />
                  ) : (
                     <div
                        className='w-full h-10 flex items-center justify-center'
                     >
                        <Loader
                           color='primary'
                        />
                     </div>
                  )
               }
            </div>
            <div
               className='w-full flex flex-col items-center gap-2'
            >
               <div
                  className='flex items-center justify-center gap-3'
               >
                  <Button
                     iconName='rotate-left'
                     primaryColor={ false }
                     backgroundColor='transparent'
                     padding={ 0 }
                     fontIconSize='extraLarge'
                     fontIconColor='secondary'
                     classNames='!w-9 !h-9 hover:bg-hover shrink-0 border border-divider'
                     borderRadius='large'
                     onClick={ () => onClickRotate('left') }
                     dataToolTipId='ams-top-tooltip'
                     dataToolTipContent='Rotate left'
                  />
                  <Button
                     iconName='rotate-right'
                     primaryColor={ false }
                     backgroundColor='transparent'
                     padding={ 0 }
                     fontIconSize='extraLarge'
                     fontIconColor='secondary'
                     classNames='!w-9 !h-9 hover:bg-hover shrink-0 border border-divider'
                     borderRadius='large'
                     onClick={ () => onClickRotate('right') }
                     dataToolTipId='ams-top-tooltip'
                     dataToolTipContent='Rotate right'
                  />
                  <Button
                     iconName='flip'
                     primaryColor={ false }
                     backgroundColor='transparent'
                     padding={ 0 }
                     fontIconSize='extraLarge'
                     fontIconColor='secondary'
                     classNames='!w-9 !h-9 hover:bg-hover shrink-0 border border-divider'
                     borderRadius='large'
                     onClick={ onClickFlip }
                     dataToolTipId='ams-top-tooltip'
                     dataToolTipContent='Flip horizontal'
                  />
                  <Button
                     iconName='minus'
                     primaryColor={ false }
                     backgroundColor='transparent'
                     padding={ 0 }
                     fontIconSize='extraLarge'
                     fontIconColor='secondary'
                     classNames='!w-9 !h-9 hover:bg-hover shrink-0 border border-divider'
                     borderRadius='large'
                     onClick={ onClickZoomOut }
                     dataToolTipId='ams-top-tooltip'
                     dataToolTipContent='Zoom out'
                  />
                  <Button
                     iconName='plus'
                     primaryColor={ false }
                     backgroundColor='transparent'
                     padding={ 0 }
                     fontIconSize='extraLarge'
                     fontIconColor='secondary'
                     classNames='!w-9 !h-9 hover:bg-hover shrink-0 border border-divider'
                     borderRadius='large'
                     onClick={ onClickZoomIn }
                     dataToolTipId='ams-top-tooltip'
                     dataToolTipContent='Zoom in'
                  />
               </div>
               {/* <div
                  className='w-full **flex items-center gap-2 hidden'
               >
                  <Button
                     iconName='minus'
                     primaryColor={ false }
                     backgroundColor='transparent'
                     padding={ 0 }
                     fontIconSize='extraLarge'
                     fontIconColor='secondary'
                     classNames='!w-9 !h-9 hover:bg-hover shrink-0'
                     borderRadius='large'
                     onClick={ onClickZoomOut }
                  />
                  <InputRange
                     withTyping={ false }
                     step={ 10 }
                     value={ rangeValue }
                     name='crop_zoom_range'
                     onChange={ handleZoomRangeChange }
                     updateStateOnValueChange
                  />
                  <Button
                     iconName='plus'
                     primaryColor={ false }
                     backgroundColor='transparent'
                     padding={ 0 }
                     fontIconSize='extraLarge'
                     fontIconColor='secondary'
                     classNames='!w-9 !h-9 hover:bg-hover shrink-0'
                     borderRadius='large'
                     onClick={ onClickZoomIn }
                  />
               </div> */}
            </div>
            <Button
               text='Undo changes'
               primaryColor={ false }
               backgroundColor='transparent'
               classNames='!h-9 mt-4'
               textSize='small'
               textColor='secondary'
               onClick={ handleClickRevertChanges }
            />
         </div>
      </CreateEditModal>
   )
}

CropImageModal.propTypes = {
   isMobile: PropTypes.bool,
   onCloseModal: PropTypes.bool,
   initialFile: PropTypes.any,
   initialImageSrc: PropTypes.string,
   onSave: PropTypes.func,
   aspectRatio: PropTypes.number,
   callbackAfterFileRead: PropTypes.func,
   showDifferentViews: PropTypes.bool,
   minCropBoxWidth: PropTypes.number,
   minCropBoxHeight: PropTypes.number,
   initialAspectRatio: PropTypes.number,
}

export default CropImageModal;
